<template>
  <AuthLayout :mode="mode">
    <Section heading="Member Document Library" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="grid gap-6">
          <Notes :title="'Disclaimer:'" :list="notesList" />
          <AppTable :mode="mode" :columns="columns">
            <template #rows>
              <tr v-for="(file, key) in files" :key="key">
                <td>
                  <FallbackText :value="file.name" :isTruncate="false" />
                </td>
                <td>
                  <FallbackText :value="file.size" :isTruncate="false" />
                </td>
                <td>
                  <div class="flex items-center">
                    <a
                      :href="file.url"
                      class="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded border transition-colors duration-200 hover:border-primary-10 hover:bg-primary-10 hover:text-primary-100"
                      :class="[
                        mode === 'dark'
                          ? 'border-dark-border'
                          : 'border-border-10',
                      ]"
                      v-tooltip="'View'"
                    >
                      <Eye :size="16" :stroke-width="1.75" />
                    </a>
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              name: 'File Name',
              size: 'Size',
              view: 'View',
            }"
            :data="files"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'name'">
                <FallbackText :value="row.name" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'size'">
                <FallbackText :value="row.size" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'view'">
                <div class="flex items-center">
                  <a
                    :href="row.url"
                    class="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded border transition-colors duration-200 hover:border-primary-10 hover:bg-primary-10 hover:text-primary-100"
                    :class="[
                      mode === 'dark'
                        ? 'border-dark-border'
                        : 'border-border-10',
                    ]"
                    v-tooltip="'View'"
                  >
                    <Eye :size="16" :stroke-width="1.75" />
                  </a>
                </div>
              </template>
            </template>
          </MobileTable>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import { Eye } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import Notes from "@C/Common/Notes.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppCard from "@C/Common/AppCard.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";

export default {
  name: "MemberLibrary",
  components: {
    AuthLayout,
    Section,
    Eye,
    Notes,
    AppTable,
    AppCard,
    FallbackText,
    MobileTable,
  },
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  data() {
    return {
      files: [],
      notesList: [
        "This material is not intended, nor should it be used, as a substitute for specific legal advice or legal opinions.",
        "Governing law may vary from state to state and applicable federal, state and local law should be considered prior to use of this information.",
        "Specific practices at the member company will also affect the applicability of the material to your situation. Legal counsel is only given in response to inquiries regarding specific situations.",
        'For such legal advice, contact your attorney or <bold>the ASCDI Office.</bold>',
      ],
      columns: [
        {
          name: " File Name",
        },
        {
          name: "Size",
        },
        {
          name: "View",
          classes: "w-[50px]",
        },
      ],
    };
  },
  mounted() {
    this.getFiles();
  },
  methods: {
    async getFiles() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/public_files`);
        if (data) {
          this.files = data;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
};
</script>
